import React from 'react';
import { AlertCircle } from 'lucide-react';
import type { Address, SupplierOrg, SupplierShipToAddress, SupplierShipToPreset } from '../../types';
import {
  formatSupplierShipToAddress,
  hasProjectSiteAddress,
  inviteShipToConfig,
  LCTF_ASPERS_SHIP_TO,
  resolveSupplierShipToAddress,
  shipToPresetLabel,
  supplierOnBothRfqWorkTypes,
  type ProjectSiteAddressInput,
  type ShipToFieldContext,
  type SupplierShipToConfig,
} from '../../utils/supplierShipTo';
import './SupplierShipToFields.css';

const EMPTY_CUSTOM: SupplierShipToAddress = {
  line1: '',
  city: '',
  state: '',
  zip: '',
};

export type SupplierShipToFieldsProps = {
  org: SupplierOrg;
  projectSite: ProjectSiteAddressInput;
  config: SupplierShipToConfig;
  onChange: (config: SupplierShipToConfig) => void;
  disabled?: boolean;
  workType: ShipToFieldContext['workType'];
  timberProject?: ShipToFieldContext['timberProject'];
  cncProject?: ShipToFieldContext['cncProject'];
};

const SupplierShipToFields: React.FC<SupplierShipToFieldsProps> = ({
  org,
  projectSite,
  config,
  onChange,
  disabled = false,
  workType,
  timberProject,
  cncProject,
}) => {
  const orgId = org.id ?? '';
  const onBothWorkTypes = supplierOnBothRfqWorkTypes(orgId, timberProject, cncProject);
  const showJobSiteOption = workType === 'cnc_quote' || onBothWorkTypes;

  const aspersPreview = formatSupplierShipToAddress(LCTF_ASPERS_SHIP_TO);
  const jobSitePreview = formatSupplierShipToAddress(
    resolveSupplierShipToAddress(
      { shipToPreset: 'job_site' },
      { workType, orgId, timberProject, cncProject, projectSite },
    ),
  );
  const jobSiteAvailable = hasProjectSiteAddress(projectSite);

  const setPreset = (preset: SupplierShipToPreset) => {
    if (preset === 'custom') {
      onChange({
        preset,
        custom: config.custom?.line1?.trim() ? config.custom : { ...EMPTY_CUSTOM },
      });
      return;
    }
    onChange({ preset });
  };

  const updateCustom = (patch: Partial<SupplierShipToAddress>) => {
    onChange({
      preset: 'custom',
      custom: { ...(config.custom ?? EMPTY_CUSTOM), ...patch },
    });
  };

  return (
    <div className="sstf">
      <span className="srfq-timber-suppliers-modal-species-label">Delivery address</span>
      {!showJobSiteOption && (
        <p className="sstf-hint">
          Job site delivery is available when this supplier is also invited on the CNC quote RFQ.
        </p>
      )}
      <div className="sstf-options" role="radiogroup" aria-label={`Delivery address for ${org.name}`}>
        <label className={`sstf-option ${config.preset === 'aspers' ? 'on' : ''}`}>
          <input
            type="radio"
            name={`ship-to-${org.id}`}
            checked={config.preset === 'aspers'}
            disabled={disabled}
            onChange={() => setPreset('aspers')}
          />
          <span className="sstf-option-body">
            <span className="sstf-option-title">{shipToPresetLabel('aspers')}</span>
            <span className="sstf-option-detail">{aspersPreview}</span>
          </span>
        </label>

        {showJobSiteOption && (
          <label className={`sstf-option ${config.preset === 'job_site' ? 'on' : ''}`}>
            <input
              type="radio"
              name={`ship-to-${org.id}`}
              checked={config.preset === 'job_site'}
              disabled={disabled}
              onChange={() => setPreset('job_site')}
            />
            <span className="sstf-option-body">
              <span className="sstf-option-title">{shipToPresetLabel('job_site')}</span>
              <span className="sstf-option-detail">
                {jobSiteAvailable ? jobSitePreview : 'No job site address on this project yet'}
              </span>
            </span>
          </label>
        )}

        <label className={`sstf-option ${config.preset === 'custom' ? 'on' : ''}`}>
          <input
            type="radio"
            name={`ship-to-${org.id}`}
            checked={config.preset === 'custom'}
            disabled={disabled}
            onChange={() => setPreset('custom')}
          />
          <span className="sstf-option-body">
            <span className="sstf-option-title">{shipToPresetLabel('custom')}</span>
          </span>
        </label>
      </div>

      {config.preset === 'job_site' && !jobSiteAvailable && (
        <p className="sstf-warn" role="status">
          <AlertCircle size={14} aria-hidden />
          Set the project address in Project Hub before publishing — suppliers need a delivery location.
        </p>
      )}

      {config.preset === 'custom' && (
        <div className="sstf-custom">
          <label className="sstf-field">
            <span>Recipient (optional)</span>
            <input
              type="text"
              className="ops-modal-input"
              value={config.custom?.recipient ?? ''}
              disabled={disabled}
              onChange={e => updateCustom({ recipient: e.target.value })}
            />
          </label>
          <label className="sstf-field">
            <span>Street</span>
            <input
              type="text"
              className="ops-modal-input"
              value={config.custom?.line1 ?? ''}
              disabled={disabled}
              onChange={e => updateCustom({ line1: e.target.value })}
            />
          </label>
          <div className="sstf-custom-row">
            <label className="sstf-field">
              <span>City</span>
              <input
                type="text"
                className="ops-modal-input"
                value={config.custom?.city ?? ''}
                disabled={disabled}
                onChange={e => updateCustom({ city: e.target.value })}
              />
            </label>
            <label className="sstf-field sstf-field--short">
              <span>State</span>
              <input
                type="text"
                className="ops-modal-input"
                value={config.custom?.state ?? ''}
                disabled={disabled}
                onChange={e => updateCustom({ state: e.target.value })}
              />
            </label>
            <label className="sstf-field sstf-field--short">
              <span>ZIP</span>
              <input
                type="text"
                className="ops-modal-input"
                value={config.custom?.zip ?? ''}
                disabled={disabled}
                onChange={e => updateCustom({ zip: e.target.value })}
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export function initialShipToConfigForOrg(
  org: SupplierOrg,
  invite: Pick<import('../../types').TimberSupplierInvite, 'shipToPreset' | 'shipToCustom'> | undefined,
  ctx: Omit<ShipToFieldContext, 'orgId'>,
): SupplierShipToConfig {
  return inviteShipToConfig(invite, { ...ctx, orgId: org.id ?? '' });
}

export function defaultShipToConfigForOrg(
  org: SupplierOrg,
  ctx: Omit<ShipToFieldContext, 'orgId'>,
): SupplierShipToConfig {
  return initialShipToConfigForOrg(org, undefined, ctx);
}

export type { ProjectSiteAddressInput };

export default SupplierShipToFields;
